import { useRef } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import type { Resolver } from 'react-hook-form'

import type { PostForm } from '@/components/retail/cms/post-form-model'
import type { ShopPostAction, ShopPostEdit } from '@/lib/api/shop-cms-api'
import { ContentMediaField } from '@/components/retail/cms/content-media-field'
import {
    PreviewButton,
    RevisionsPanel,
} from '@/components/retail/cms/content-tools'
import {
    createPostSchema,
    emptyPostForm,
    formToInput,
    postFormField,
    postToForm,
} from '@/components/retail/cms/post-form-model'
import { PostGroupsField } from '@/components/retail/cms/post-groups-field'
import {
    ErrorText,
    RichTextField,
    SwitchField,
    TextField,
} from '@/components/retail/form-bits'
import { shopFieldErrors } from '@/components/retail/shop-errors'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    useDeleteShopPost,
    useSaveShopPost,
    useShopPostCategories,
} from '@/hooks/use-shop-posts-query'
import { m } from '@/lib/paraglide/messages'

type Mode = 'draft' | 'publish'

/** Trạng thái bài cho nhãn: đã đăng / đã đăng + nháp chờ đăng / nháp. */
export function postStatusLabel(status: string, hasDraft: boolean): string {
    if (status !== 'published') return m.cmsPost_status_draft()
    return hasDraft ? m.cmsPost_status_pending() : m.cmsPost_status_published()
}

/**
 * Màn soạn bài viết. Bài mới: "Lưu nháp" tạo bài (chưa lên web). Bài đã
 * đăng: "Lưu nháp" chỉ ghi nháp chờ đăng, web giữ bản cũ tới khi bấm "Đăng".
 */
export function PostEditor({
    post,
    canWrite,
}: {
    post?: ShopPostEdit
    canWrite: boolean
}) {
    const navigate = useNavigate()
    const save = useSaveShopPost()
    const remove = useDeleteShopPost()
    const categories = useShopPostCategories().data?.data ?? []
    const mode = useRef<Mode>('draft')
    // Schema theo nút bấm: "Đăng" đòi thêm mô tả ngắn + chuyên mục.
    const resolver: Resolver<PostForm> = (values, ctx, opts) =>
        (
            zodResolver(
                createPostSchema(mode.current === 'publish'),
            ) as unknown as Resolver<PostForm>
        )(values, ctx, opts)
    const form = useForm<PostForm>({
        resolver,
        defaultValues: post ? postToForm(post) : emptyPostForm(),
    })

    const fail = (err: unknown) => {
        for (const [field, message] of Object.entries(
            shopFieldErrors(err, m.cmsPost_save_failed()),
        )) {
            form.setError(
                field === 'form'
                    ? 'root.server'
                    : (postFormField(field) as keyof PostForm),
                {
                    type: 'server',
                    message,
                },
            )
        }
    }

    const submit = (next: Mode) => {
        mode.current = next
        void form.handleSubmit(async (values) => {
            try {
                let current = post
                if (!current) {
                    const created = await save.mutateAsync({
                        kind: 'create',
                        input: formToInput(values),
                    })
                    current = created.data
                    if (!current) return
                    if (next === 'draft') {
                        toast.success(m.cmsPost_saved_draft())
                        void navigate({
                            to: '/retail/posts/$postId',
                            params: { postId: current.id },
                        })
                        return
                    }
                }
                const input = formToInput(values, current.version)
                const res = await save.mutateAsync(
                    next === 'publish'
                        ? { kind: 'publish', id: current.id, input }
                        : { kind: 'save', id: current.id, input },
                )
                toast.success(
                    next === 'publish'
                        ? m.cmsPost_published()
                        : m.cmsPost_saved_draft(),
                )
                if (!post && res.data)
                    void navigate({
                        to: '/retail/posts/$postId',
                        params: { postId: res.data.id },
                    })
            } catch (err) {
                fail(err)
            }
        })()
    }

    const action = async (kind: ShopPostAction) => {
        if (!post) return
        try {
            await save.mutateAsync({
                kind,
                id: post.id,
                input: {
                    ...formToInput(form.getValues()),
                    version: post.version,
                },
            })
            toast.success(
                kind === 'unpublish'
                    ? m.cmsPost_unpublished()
                    : m.cmsPost_draft_discarded(),
            )
        } catch (err) {
            fail(err)
        }
    }

    const del = async () => {
        if (
            !post ||
            !window.confirm(
                m.cmsPost_delete_confirm({ title: post.fields.title }),
            )
        )
            return
        try {
            await remove.mutateAsync(post.id)
            toast.success(m.cmsPost_deleted())
            void navigate({ to: '/retail/posts' })
        } catch (err) {
            fail(err)
        }
    }

    const busy = save.isPending || remove.isPending
    return (
        <FormProvider {...form}>
            <form
                noValidate
                onSubmit={(e) => {
                    e.preventDefault()
                    submit('draft')
                }}
                className="space-y-4"
            >
                <div className="flex flex-wrap items-center gap-2">
                    <Badge
                        variant={
                            post?.status === 'published'
                                ? 'default'
                                : 'secondary'
                        }
                    >
                        {postStatusLabel(
                            post?.status ?? 'draft',
                            post?.has_draft ?? false,
                        )}
                    </Badge>
                    {post?.status === 'published' && (
                        <span className="text-sm text-muted-foreground">
                            {m.cmsPost_live_url({ slug: post.live_slug })}
                        </span>
                    )}
                </div>
                <fieldset
                    disabled={!canWrite}
                    className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]"
                >
                    <div className="space-y-4">
                        <Card>
                            <CardContent className="space-y-4 pt-6">
                                <TextField
                                    name="title"
                                    label={m.cmsPost_title()}
                                />
                                <TextField
                                    name="slug"
                                    label={m.cmsPost_slug()}
                                    hint={m.cmsPost_slug_hint()}
                                />
                                <TextField
                                    name="excerpt"
                                    label={m.cmsPost_excerpt()}
                                    rows={3}
                                />
                                <RichTextField
                                    name="content_html"
                                    label={m.cmsPost_content()}
                                    hint={m.cmsPost_content_hint()}
                                    readOnly={!canWrite}
                                />
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    {m.cmsPost_seo()}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <TextField
                                    name="seo_title"
                                    label={m.cmsPost_seo_title()}
                                />
                                <TextField
                                    name="seo_description"
                                    label={m.cmsPost_seo_description()}
                                    rows={2}
                                />
                                <ContentMediaField
                                    name="og"
                                    label={m.cmsPost_og_image()}
                                    hint={m.cmsPost_og_hint()}
                                />
                            </CardContent>
                        </Card>
                    </div>
                    <Card className="h-fit">
                        <CardContent className="space-y-4 pt-6">
                            <Field
                                name="category_id"
                                label={m.cmsPost_category()}
                            >
                                <Controller
                                    control={form.control}
                                    name="category_id"
                                    render={({ field }) => (
                                        <Select
                                            value={field.value || undefined}
                                            onValueChange={field.onChange}
                                        >
                                            <SelectTrigger
                                                id="category_id"
                                                className="w-full"
                                            >
                                                <SelectValue
                                                    placeholder={m.cmsPost_category_pick()}
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {categories.map((c) => (
                                                    <SelectItem
                                                        key={c.id}
                                                        value={c.id}
                                                    >
                                                        {c.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </Field>
                            <ContentMediaField
                                name="cover"
                                label={m.cmsPost_cover()}
                            />
                            <SwitchField
                                name="featured"
                                label={m.cmsPost_featured()}
                            />
                            <Field
                                name="published_at"
                                label={m.cmsPost_published_at()}
                            >
                                <Input
                                    id="published_at"
                                    type="datetime-local"
                                    {...form.register('published_at')}
                                />
                            </Field>
                            <PostGroupsField />
                        </CardContent>
                    </Card>
                </fieldset>
                <ErrorText text={form.formState.errors.root?.server?.message} />
                {canWrite && (
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            disabled={busy}
                            onClick={() => submit('publish')}
                        >
                            {m.cmsPost_publish()}
                        </Button>
                        <Button type="submit" variant="outline" disabled={busy}>
                            {m.cmsPost_save_draft()}
                        </Button>
                        {post?.has_draft && (
                            <Button
                                type="button"
                                variant="outline"
                                disabled={busy}
                                onClick={() => void action('discard-draft')}
                            >
                                {m.cmsPost_discard_draft()}
                            </Button>
                        )}
                        {post?.status === 'published' && (
                            <Button
                                type="button"
                                variant="outline"
                                disabled={busy}
                                onClick={() => void action('unpublish')}
                            >
                                {m.cmsPost_unpublish()}
                            </Button>
                        )}
                        {post && (
                            <Button
                                type="button"
                                variant="destructive"
                                disabled={busy}
                                onClick={() => void del()}
                            >
                                {m.common_delete()}
                            </Button>
                        )}
                    </div>
                )}
            </form>
            {post && (
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                    <RevisionsPanel
                        doc="posts"
                        id={post.id}
                        version={post.version}
                        canWrite={canWrite}
                    />
                    <PreviewButton doc="posts" id={post.id} />
                </div>
            )}
        </FormProvider>
    )
}
